import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import clsx from "clsx";

/* ── Helpers ──────────────────────────────────────────────────────────────── */
function AccuracyBar({ value, color }) {
  return (
    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${value}%`, background: color }}
      />
    </div>
  );
}

function TrendIcon({ trend }) {
  if (trend === "up")     return <span className="text-green-400 text-xs font-bold">↑</span>;
  if (trend === "down")   return <span className="text-red-400 text-xs font-bold">↓</span>;
  return <span className="text-gray-500 text-xs">→</span>;
}

function MiniSparkline({ scores }) {
  if (!scores || scores.length < 2) return null;
  const w = 60, h = 24;
  const min = Math.min(...scores), max = Math.max(...scores);
  const range = max - min || 1;
  const pts = scores.map((s, i) => {
    const x = (i / (scores.length - 1)) * w;
    const y = h - ((s - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={w} height={h} className="opacity-60">
      <polyline points={pts} fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/* ── Category card ────────────────────────────────────────────────────────── */
function CategoryCard({ stat, rank }) {
  const isStrength = stat.accuracy >= 75;
  const isWeakness = stat.accuracy < 55;
  const color = isStrength ? "#22c55e" : isWeakness ? "#ef4444" : "#f59e0b";
  const label = isStrength ? "Stärke" : isWeakness ? "Schwäche" : "Mittel";
  const bg    = isStrength ? "bg-green-500/10 border-green-500/30" : isWeakness ? "bg-red-500/10 border-red-500/30" : "bg-yellow-500/10 border-yellow-500/30";

  return (
    <div className={clsx("border rounded-2xl p-4 space-y-3", bg)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">{rank <= 3 ? ["🥇","🥈","🥉"][rank-1] : "📊"}</span>
          <div>
            <p className="font-bold text-sm text-white">{stat.category}</p>
            <p className="text-[10px] text-gray-500">{stat.attempts} Quiz{stat.attempts !== 1 ? "ze" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <TrendIcon trend={stat.trend} />
          <span className="text-lg font-black" style={{ color }}>{stat.accuracy}%</span>
        </div>
      </div>
      <AccuracyBar value={stat.accuracy} color={color} />
      <div className="flex items-center justify-between">
        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${color}20`, color }}>{label}</span>
        {stat.recentAvg !== stat.accuracy && (
          <span className="text-[10px] text-gray-500">Zuletzt: {stat.recentAvg}%</span>
        )}
      </div>
    </div>
  );
}

/* ── Recent trend line chart ──────────────────────────────────────────────── */
function TrendChart({ data }) {
  if (!data || data.length === 0) return null;
  const w = 300, h = 80;
  const min = Math.min(...data.map((d) => d.score));
  const max = Math.max(...data.map((d) => d.score));
  const range = max - min || 1;

  const pts = data.map((d, i) => {
    const x = (i / Math.max(data.length - 1, 1)) * (w - 20) + 10;
    const y = h - ((d.score - min) / range) * (h - 20) - 10;
    return { x, y, ...d };
  });

  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
      <p className="text-xs font-bold text-gray-400 mb-3">Letzten {data.length} Quizze</p>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
        {/* Grid lines */}
        {[25, 50, 75, 100].map((v) => {
          const y = h - ((v - min) / range) * (h - 20) - 10;
          if (y < 0 || y > h) return null;
          return (
            <g key={v}>
              <line x1={10} y1={y} x2={w - 10} y2={y} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
              <text x={4} y={y + 3} fontSize="7" fill="rgba(255,255,255,0.2)">{v}</text>
            </g>
          );
        })}
        {/* Gradient fill */}
        <defs>
          <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${path} L${pts[pts.length-1].x},${h} L${pts[0].x},${h} Z`} fill="url(#trendGrad)" />
        <path d={path} fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {/* Dots */}
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill={p.score >= 75 ? "#22c55e" : p.score < 55 ? "#ef4444" : "#f59e0b"} />
        ))}
      </svg>
    </div>
  );
}

/* ── Skeleton ─────────────────────────────────────────────────────────────── */
function Skeleton({ className }) {
  return <div className={clsx("animate-pulse rounded-xl bg-white/5", className)} />;
}

/* ── Main ─────────────────────────────────────────────────────────────────── */
export default function Analysis() {
  const { data, isLoading } = useQuery({
    queryKey: ["analysis"],
    queryFn: () => api.get("/analysis/me").then((r) => r.data),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-4 animate-fade-in">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
      </div>
    );
  }

  const { totalAttempts, strengths, weaknesses, categoryStats, recentTrend, recommendation } = data || {};

  if (totalAttempts === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 animate-fade-in">
        <div className="w-20 h-20 rounded-2xl bg-violet-500/20 flex items-center justify-center text-4xl">🧠</div>
        <div className="text-center max-w-xs">
          <h2 className="font-black text-lg text-white">Keine Daten</h2>
          <p className="text-sm text-gray-400 mt-1">{recommendation}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in max-w-2xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-xl font-black flex items-center gap-2">
          <span className="text-2xl">🧠</span>
          <span>Lernanalyse</span>
        </h1>
        <p className="text-xs text-gray-400 mt-0.5">{totalAttempts} Quizze analysiert</p>
      </div>

      {/* Recommendation banner */}
      <div className="bg-violet-500/10 border border-violet-500/30 rounded-2xl p-4 flex gap-3">
        <span className="text-2xl flex-shrink-0">💡</span>
        <p className="text-sm text-gray-200">{recommendation}</p>
      </div>

      {/* Strengths */}
      {strengths?.length > 0 && (
        <div>
          <h2 className="text-sm font-black text-green-400 mb-2 flex items-center gap-1.5">
            <span>💪</span> Stärken ({strengths.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {strengths.map((s, i) => <CategoryCard key={s.category} stat={s} rank={i + 1} />)}
          </div>
        </div>
      )}

      {/* Weaknesses */}
      {weaknesses?.length > 0 && (
        <div>
          <h2 className="text-sm font-black text-red-400 mb-2 flex items-center gap-1.5">
            <span>🎯</span> Verbesserungspotenzial ({weaknesses.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {weaknesses.map((s, i) => <CategoryCard key={s.category} stat={s} rank={i + 1} />)}
          </div>
        </div>
      )}

      {/* All categories */}
      <div>
        <h2 className="text-sm font-black text-gray-400 mb-2 flex items-center gap-1.5">
          <span>📊</span> Alle Kategorien
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {categoryStats?.map((s, i) => <CategoryCard key={s.category} stat={s} rank={i + 1} />)}
        </div>
      </div>

      {/* Trend chart */}
      {recentTrend?.length >= 2 && <TrendChart data={recentTrend} />}

      {/* Legend */}
      <div className="flex items-center gap-4 text-[11px] text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" /> Stärke ≥75%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" /> Mittel 55–74%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" /> Schwäche &lt;55%</span>
      </div>
    </div>
  );
}
